// Typed client for /api/v1, shared by the web app and the mini program.
// Platforms differ only in how a request is sent, so they plug in a Transport.
// Contract: platform/docs/api-v1/learning.md

export interface TransportRequest {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  path: string
  query?: Record<string, string | number | undefined>
  body?: unknown
}

export interface TransportResponse {
  status: number
  data: unknown
}

export type Transport = (request: TransportRequest) => Promise<TransportResponse>

export class ApiError extends Error {
  constructor(public status: number, public code: string, public details?: unknown) {
    super(code)
  }
}

export interface AccountUser {
  id: string
  kind?: string
  email?: string | null
  displayName?: string
  avatar?: string
}

import { createAccountApi } from './account'
import { createMembershipApi } from './membership'
import { createShopApi } from './shop'
import { createQuizModesApi } from './quiz-modes'
import { createMapApi } from './map'
import { createInterviewApi } from './interview'
import { createGuideApi } from './guide'
import { createPracticeApi } from './practice'
export type { Call } from './call'
export type * from './shop'
export type * from './membership'
export type * from './map'
export type * from './practice'

export type QuestionType = '单选题' | '多选题' | '判断题'
export type AnswerMode = 'practice' | 'review' | 'exam' | 'specialty' | 'learn'

export interface Question {
  id: string
  revision: number
  type: QuestionType
  stem: string
  options: string[]
  examYears: number[]
}

export interface QuestionState {
  attempts: number
  correctCount: number
  wrongCount: number
  mastered: boolean
  remediationStage: number
  dueAtSeq: number | null
  dueAt: string | null
  everWrong: boolean
}

export interface GradeResult {
  correct: boolean
  answer: number[]
  explanation: string | null
  optionExplanations: Record<string, string> | null
}

export interface AnswerResult extends GradeResult {
  state: QuestionState
}

export interface VideoCourse { id: string; title: string; color?: string; [key: string]: unknown }
export interface VideoLesson {
  id: string; course: string; chapter: string; title: string; duration: string;
  cover?: string; free?: boolean; order?: number;
}

export interface NativeLearningCard { id: string; title: string; image?: string; thumb?: string; subject?: string; chapter?: string; questionCount?: number }
export interface NativeSpecialtyItem { id: string; name: string; short?: string; image?: string; tags?: string[]; features?: string[]; questionFingerprints?: string[] }
export interface NativeLearningCatalog {
  cards?: NativeLearningCard[]
  total?: number
  section?: string
  title?: string
  description?: string
  items?: NativeSpecialtyItem[]
}

export interface Catalog {
  subjects: { subject: string; chapters: { chapter: string; questionCount: number }[] }[]
  bankRevision: string
}

export interface ChapterSummary {
  chapter: string
  total: number
  done: number
  correct: number
  wrong: number
  mastered: number
  due: number
  attempts: number
  correctAttempts: number
}

export interface Summary {
  subjects: { subject: string; total: number; done: number; correct: number; wrong: number; mastered: number; due: number; attempts: number; correctAttempts: number; chapters: ChapterSummary[] }[]
  streakDays: number
  todayAttempts: number
  today: string
}

export interface QuestionStateEntry extends QuestionState {
  questionId: string
  lastCorrect: boolean | null
  lastAnsweredAt: string | null
}

export interface LegacyHistory {
  total: number
  mastered: number
  wrong: number
  items: { subject: string; chapter: string; stem: string; attempts: number; mastered: boolean; wrong: boolean; lastAnsweredAt: string | null }[]
}

export interface QuestionWithState {
  question: Question
  subject: string
  chapter: string
  state: QuestionStateEntry
}

export interface SubmitAnswer {
  clientAttemptId: string
  questionId: string
  revision: number
  selected: number[]
  mode: AnswerMode
  answeredAt?: string
}

/** Unique id for an attempt, so a retried submission is recorded once. */
export function newAttemptId(): string {
  const random = globalThis.crypto?.randomUUID?.()
  if (random) return random
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
}

export function createApiClient(transport: Transport) {
  async function call<T>(request: TransportRequest): Promise<T> {
    const response = await transport(request)
    if (response.status >= 200 && response.status < 300) return response.data as T
    // Every route answers errors as {error: "CODE", message, ...details}; the object form is accepted too.
    const error = (response.data as { error?: string | { code?: string } } | null)?.error
    const code = typeof error === 'string' ? error : error?.code
    throw new ApiError(response.status, code || `HTTP_${response.status}`, response.data)
  }

  return {
    /** Profile, password, sessions, signup, reset, deletion (existing routes). */
    accountCenter: createAccountApi(call),
    /** Plans, orders, payment, status (existing routes). */
    membership: createMembershipApi(call),
    shop: createShopApi(call),
    /** Practice modes beyond chapter practice: bookmarks, reports, custom papers. */
    quizModes: createQuizModesApi(call),
    map: createMapApi(call),
    interview: createInterviewApi(call),
    guide: createGuideApi(call),
    /** Server-driven practice sessions (chapter, due review, wrong book) and the practice counters. */
    practice: createPracticeApi(call),
    // Accounts, sessions, membership and payments stay on the existing routes (same users, same sessions).
    account: {
      session: () => call<{ authenticated: boolean; user: AccountUser }>({ method: 'GET', path: '/api/auth/session' }),
      passwordLogin: (email: string, password: string) =>
        call<{ ok: true; user: AccountUser }>({ method: 'POST', path: '/api/auth/password/login', body: { email, password } }),
      requestLoginCode: (email: string) =>
        call<{ ok: true; accepted: true; expiresIn: number }>({ method: 'POST', path: '/api/auth/email/login-request', body: { email } }),
      codeLogin: (email: string, code: string) =>
        call<{ ok: true; user: AccountUser }>({ method: 'POST', path: '/api/auth/email/code-login', body: { email, code } }),
      logout: () => call<{ ok: true }>({ method: 'POST', path: '/api/auth/logout', body: {} }),
      /** Guest session: lets a visitor grade answers (rate limited per session) without an account. */
      guest: () => call<{ ok: true; user: AccountUser }>({ method: 'POST', path: '/api/auth/guest', body: {} }),
    },

    health: () => call<{ ok: boolean; database: boolean }>({ method: 'GET', path: '/api/v1/health' }),
    me: () => call<{ user: { id: string; kind: string; guest: boolean } }>({ method: 'GET', path: '/api/v1/me' }),

    catalog: () => call<Catalog>({ method: 'GET', path: '/api/v1/catalog' }),
    videoLessons: () => call<{ courses: VideoCourse[]; videos: VideoLesson[] }>({ method: 'GET', path: '/api/video/lessons' }),
    nativeLearning: (kind: string, section?: string) => call<NativeLearningCatalog>({ method: 'GET', path: '/api/native/v1/learning', query: { kind, section } }),
    questions: (subject: string, chapter: string) =>
      call<{ questions: Question[] }>({ method: 'GET', path: '/api/v1/questions', query: { subject, chapter } }),
    /** Up to 200 questions by id, including ones outside the default practice pool. */
    questionsByIds: (ids: string[]) =>
      call<{ questions: (Question & { subject: string; chapter: string })[]; missing: string[] }>(
        { method: 'GET', path: '/api/v1/questions', query: { ids: ids.join(',') } }),
    grade: (questionId: string, revision: number, selected: number[]) =>
      call<GradeResult>({ method: 'POST', path: '/api/v1/grade', body: { questionId, revision, selected } }),
    answer: (input: SubmitAnswer) => call<AnswerResult>({ method: 'POST', path: '/api/v1/answers', body: input }),

    summary: () => call<Summary>({ method: 'GET', path: '/api/v1/learning/summary' }),
    states: (subject: string, chapter?: string) =>
      call<{ states: QuestionStateEntry[] }>({ method: 'GET', path: '/api/v1/learning/states', query: { subject, chapter } }),
    wrong: (subject?: string) => call<{ items: QuestionWithState[] }>({ method: 'GET', path: '/api/v1/learning/wrong', query: { subject } }),
    reviewQueue: (subject?: string, limit = 20) =>
      call<{ items: QuestionWithState[] }>({ method: 'GET', path: '/api/v1/learning/review-queue', query: { subject, limit } }),
    positions: () => call<{ positions: { subject: string; chapter: string; questionId: string | null; updatedAt: string }[] }>(
      { method: 'GET', path: '/api/v1/learning/positions' }),
    savePosition: (subject: string, chapter: string, questionId: string) =>
      call({ method: 'PUT', path: '/api/v1/learning/positions', body: { subject, chapter, questionId } }),
    bookmarks: () => call<{ bookmarks: { kind: string; targetId: string; active: boolean; updatedAt: string }[] }>(
      { method: 'GET', path: '/api/v1/learning/bookmarks' }),
    setBookmark: (kind: string, targetId: string, active: boolean) =>
      call({ method: 'PUT', path: '/api/v1/learning/bookmarks', body: { kind, targetId, active, updatedAt: new Date().toISOString() } }),
    addStudyTime: (subject: string, chapter: string, seconds: number) =>
      call({ method: 'POST', path: '/api/v1/learning/study-time', body: { subject, chapter, seconds } }),
    resetLearning: () => call({ method: 'POST', path: '/api/v1/learning/reset', body: {} }),
    /** Practice on questions later removed from the bank, kept from the old system as history. */
    legacyHistory: () => call<LegacyHistory>({ method: 'GET', path: '/api/v1/learning/legacy-history' }),
  }
}

export type ApiClient = ReturnType<typeof createApiClient>

/** Browser transport: same-origin fetch with the session cookie. extraHeaders go on every request (client surface and version). */
export function fetchTransport(baseUrl = '', extraHeaders: Record<string, string> = {}): Transport {
  return async ({ method, path, query, body }) => {
    const url = new URL(baseUrl + path, globalThis.location?.origin ?? 'http://localhost')
    for (const [key, value] of Object.entries(query ?? {})) if (value !== undefined) url.searchParams.set(key, String(value))
    const response = await fetch(url, {
      method,
      credentials: 'include',
      headers: body === undefined ? { ...extraHeaders } : { ...extraHeaders, 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
    const text = await response.text()
    let data: unknown = null
    try { data = text ? JSON.parse(text) : null } catch { data = null }
    return { status: response.status, data }
  }
}
